/**
 * FeatureGuard Component
 *
 * Protects routes and UI sections based on feature flags and permissions.
 *
 * Usage:
 *   // Route protection (redirect to access denied)
 *   <Route
 *     path="/tools"
 *     element={
 *       <FeatureGuard feature="tools" action="view">
 *         <ToolsPage />
 *       </FeatureGuard>
 *     }
 *   />
 *
 *   // UI section protection (render nothing if denied)
 *   <FeatureGuard feature="tools" action="create" fallback={null}>
 *     <CreateToolButton />
 *   </FeatureGuard>
 *
 *   // With custom fallback
 *   <FeatureGuard feature="tools" action="marketplace" fallback={<UpgradePrompt />}>
 *     <MarketplaceSection />
 *   </FeatureGuard>
 */

import React, { type ReactNode } from 'react'
import { type ActionType } from '../../hooks/useCanAccess'

// =============================================================================
// TYPES
// =============================================================================

export interface FeatureGuardProps {
  /**
   * Feature ID to check (e.g., 'tools', 'agent-studio', 'admin').
   * Not required if using flagKey.
   */
  feature?: string

  /**
   * Action to check (default: 'view').
   * Not required if using flagKey.
   */
  action?: ActionType

  /**
   * Direct feature flag key to check (e.g., 'tools_manage_access').
   * If provided, feature and action are ignored.
   */
  flagKey?: string

  /**
   * Children to render if access is granted.
   */
  children: ReactNode

  /**
   * Fallback to render if access is denied.
   * - undefined: Render PermissionDenied page (default for routes)
   * - null: Render nothing (good for hiding buttons)
   * - ReactNode: Render custom fallback
   */
  fallback?: ReactNode

  /**
   * Whether to redirect to a different route on denied.
   * If true, redirects to /access-denied.
   */
  redirectOnDenied?: boolean

  /**
   * URL to redirect to when access is denied.
   */
  redirectTo?: string

  /**
   * Whether to show loading state while checking access.
   * Default: true (prevents flash of PermissionDenied while flags load)
   */
  showLoading?: boolean

  /**
   * Loading component to show while checking access.
   */
  loadingComponent?: ReactNode
}

// =============================================================================
// COMPONENT
// =============================================================================

export const FeatureGuard: React.FC<FeatureGuardProps> = ({ children }) => {
  // ---------------------------------------------------------------------------
  // FEATURE FLAG / RBAC CHECKS DISABLED — single nullify point for route guards.
  //
  // Every <FeatureGuard ...> wrapper in App.tsx (feature= / action= / flagKey=)
  // is now a pass-through that just renders its children. Loading states,
  // permission-denied redirects, fallbacks, and platform-admin bypass are all
  // skipped.
  //
  // To re-enable gating, restore the original implementation:
  //
  //   const location = useLocation()
  //   const { canAccess, isFlagEnabled, isLoading, isPlatformAdmin } = useCanAccess()
  //   const hasAccess = useMemo(() => {
  //     if (isPlatformAdmin) return true
  //     if (flagKey) return isFlagEnabled(flagKey as any)
  //     if (feature) return canAccess(feature, action)
  //     return true
  //   }, [canAccess, isFlagEnabled, feature, action, flagKey, isPlatformAdmin])
  //   if (isLoading && showLoading) {
  //     return loadingComponent ? <>{loadingComponent}</> : null
  //   }
  //   if (hasAccess) return <>{children}</>
  //   if (redirectOnDenied || redirectTo) {
  //     const destination = redirectTo || '/access-denied'
  //     return <Navigate to={destination} state={{ from: location, feature, action }} replace />
  //   }
  //   if (fallback !== undefined) return <>{fallback}</>
  //   return <PermissionDenied />
  // ---------------------------------------------------------------------------
  return <>{children}</>
}

// =============================================================================
// CONVENIENCE COMPONENTS
// =============================================================================

/**
 * Guard for view access (most common case).
 */
export const ViewGuard: React.FC<Omit<FeatureGuardProps, 'action'>> = (props) => (
  <FeatureGuard {...props} action="view" />
)

/**
 * Guard for create access.
 */
export const CreateGuard: React.FC<Omit<FeatureGuardProps, 'action'>> = (props) => (
  <FeatureGuard {...props} action="create" />
)

/**
 * Guard for edit access.
 */
export const EditGuard: React.FC<Omit<FeatureGuardProps, 'action'>> = (props) => (
  <FeatureGuard {...props} action="edit" />
)

/**
 * Guard for delete access.
 */
export const DeleteGuard: React.FC<Omit<FeatureGuardProps, 'action'>> = (props) => (
  <FeatureGuard {...props} action="delete" />
)

export default FeatureGuard
